<template>
  <template v-if="setpointValues.length">
    <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <!-- Header: label + period buttons -->
      <div class="flex items-center justify-between mb-3">
        <span
          class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
          <ControlIcon name="activity" size="0.875rem" />
          {{ tt('Activity', 'Dejavnost') }}
        </span>
        <div class="flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
          <button
            v-for="p in PERIODS"
            :key="p.value"
            @click="selectPeriod(p.value)"
            class="px-2.5 py-0.5 text-xs transition-colors"
            :class="
              selectedPeriod === p.value
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            ">
            {{ p.label }}
          </button>
        </div>
      </div>

      <!-- Loading spinner — the drawn set's `spinner` (P11 sweep, card t_bd3aea71) -->
      <div v-if="isLoading" class="flex justify-center py-4">
        <ControlIcon name="spinner" size="1.25rem" class="animate-spin text-gray-400 dark:text-gray-600" />
      </div>

      <!-- Timeline rows -->
      <div v-else class="space-y-3">
        <div v-for="val in setpointValues" :key="val.id" class="space-y-0.5">
          <!-- Value name + current value -->
          <div class="flex items-baseline justify-between px-0.5">
            <span class="text-xs text-gray-500 dark:text-gray-400">{{ val.name }}</span>
            <span class="text-xs font-medium text-gray-600 dark:text-gray-300">{{ formatCurrentValue(val) }}</span>
          </div>

          <!-- Segmented bar -->
          <div
            class="relative h-6 flex rounded-md overflow-hidden"
            :class="transparentTrack ? 'bg-transparent' : 'bg-gray-100 dark:bg-gray-700/60'">
            <template v-if="getSegments(val.id).length">
              <div
                v-for="(seg, idx) in getSegments(val.id)"
                :key="idx"
                class="relative h-full cursor-default select-none"
                :style="{
                  width: `${seg.widthPct}%`,
                  minWidth: '2px',
                  backgroundColor: seg.color,
                  opacity: seg.noData ? 0.22 : 1,
                  borderRight:
                    idx < getSegments(val.id).length - 1 && seg.color !== 'transparent'
                      ? '1px solid rgba(0,0,0,0.20)'
                      : 'none',
                }"
                @mouseenter="showTooltip($event, seg, val)"
                @mouseleave="hideTooltip"
                @mousemove="updateTooltipPos($event)" />
            </template>
            <div v-else class="absolute inset-0 flex items-center justify-center">
              <span class="text-xs text-gray-400 dark:text-gray-600">{{
                tt('No data in range', 'Ni podatkov v tem razponu')
              }}</span>
            </div>
          </div>

          <!-- Time axis -->
          <div class="flex justify-between text-[10px] text-gray-400 dark:text-gray-600 px-0.5 pt-0.5">
            <span>{{ formatAxisTime(periodStart) }}</span>
            <span>{{ formatAxisTime(periodMid) }}</span>
            <span>{{ tt('Now', 'Zdaj') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating tooltip (body-level to avoid overflow clipping) -->
    <Teleport to="body">
      <div
        v-if="tooltip.visible"
        class="fixed z-[9999] bg-gray-900/95 dark:bg-gray-800/95 text-white rounded-lg shadow-xl px-3 py-2.5 text-xs pointer-events-none backdrop-blur-sm"
        :style="{ top: `${tooltip.y}px`, left: `${tooltip.x}px`, transform: 'translate(-50%, calc(-100% - 12px))' }">
        <div class="font-semibold text-white mb-1">{{ tooltip.valueName }}</div>
        <div class="flex items-center gap-1.5">
          <div class="w-2.5 h-2.5 rounded-sm flex-shrink-0" :style="{ backgroundColor: tooltip.color }"></div>
          <span class="text-gray-100">{{ tooltip.valueLabel }}</span>
        </div>
        <div class="text-gray-400 mt-1">{{ tooltip.timeRange }}</div>
        <div class="text-gray-500">{{ tooltip.duration }}</div>
        <span
          v-if="tooltip.source"
          class="mt-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium"
          :class="sourceClass(tooltip.source)">
          {{ tooltip.source }}
        </span>
        <!-- Arrow -->
        <div
          class="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-full w-0 h-0"
          style="
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid rgba(17, 24, 39, 0.95);
          " />
      </div>
    </Teleport>
  </template>
</template>

<script setup lang="ts">
import { tt } from '../../utils/consoleText';
import { formatDate, formatTime as formatTimeLocale } from '../../utils/formatDate';
import ControlIcon from '../device-icons/ControlIcon.vue';
import { ref, computed, watch } from 'vue';
import { apiFetch } from '@/services/api';
import type { DeviceValue } from '@/types';

const props = withDefaults(
  defineProps<{
    deviceValues: DeviceValue[];
    deviceId: string;
    /** Device's characteristic "active" color — used for on/brightness segments (e.g. 'rgb(250,204,21)' for lights) */
    accentColor?: string;
    /** Color for inactive or false segments. */
    inactiveColor?: string;
    /** Remove the default neutral track fill behind segments. */
    transparentTrack?: boolean;
    /** Restrict to these loggingType values. When omitted, all non-None logged values are shown. */
    filterTypes?: string[];
  }>(),
  { accentColor: 'rgb(250, 204, 21)', inactiveColor: '#6b7280', transparentTrack: false },
);

// ── Periods ───────────────────────────────────────────────────────────────
const PERIODS = [
  { label: '1h', value: '1h', ms: 3_600_000 },
  { label: '6h', value: '6h', ms: 21_600_000 },
  { label: '24h', value: '24h', ms: 86_400_000 },
  { label: '7d', value: '7d', ms: 604_800_000 },
] as const;

const selectedPeriod = ref<string>('24h');
const isLoading = ref(false);

// ── Types ─────────────────────────────────────────────────────────────────
interface Segment {
  startTime: Date;
  endTime: Date;
  value: unknown;
  source: string | null;
  widthPct: number;
  color: string;
  noData?: boolean;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  valueName: string;
  valueLabel: string;
  timeRange: string;
  duration: string;
  source: string | null;
  color: string;
}

// ── Data ──────────────────────────────────────────────────────────────────
const segmentsByValue = ref<Record<string, Segment[]>>({});

const setpointValues = computed(() =>
  (props.deviceValues ?? []).filter((v) =>
    props.filterTypes ? props.filterTypes.includes(v.loggingType!) : v.loggingType && v.loggingType !== 'None',
  ),
);

const historyRequestKey = computed(() =>
  [props.deviceId, selectedPeriod.value, ...setpointValues.value.map((value) => value.id)].join(':'),
);

const periodMs = computed(() => PERIODS.find((p) => p.value === selectedPeriod.value)?.ms ?? 86_400_000);
const periodEnd = computed(() => new Date());
const periodStart = computed(() => new Date(periodEnd.value.getTime() - periodMs.value));
const periodMid = computed(() => new Date((periodStart.value.getTime() + periodEnd.value.getTime()) / 2));

// ── Fetch ─────────────────────────────────────────────────────────────────
async function fetchHistory() {
  if (!setpointValues.value.length) return;
  isLoading.value = true;
  const now = new Date();
  const start = new Date(now.getTime() - periodMs.value);
  const results: Record<string, Segment[]> = {};

  await Promise.all(
    setpointValues.value.map(async (val) => {
      try {
        const resp = await apiFetch(
          `/values/${val.id}/history?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(now.toISOString())}`,
        );
        results[val.id] = buildSegments(val, resp?.data ?? [], start, now);
      } catch {
        results[val.id] = [];
      }
    }),
  );

  segmentsByValue.value = results;
  isLoading.value = false;
}

function selectPeriod(p: string) {
  selectedPeriod.value = p;
}

// ── Build segments ────────────────────────────────────────────────────────
function buildSegments(
  val: DeviceValue,
  dataPoints: { timestamp: string; value: unknown; source?: string }[],
  start: Date,
  end: Date,
): Segment[] {
  const totalMs = end.getTime() - start.getTime();
  const segments: Segment[] = [];

  if (!dataPoints.length) {
    segments.push({
      startTime: start,
      endTime: end,
      value: null,
      source: null,
      widthPct: 100,
      color: '#9ca3af',
      noData: true,
    });
    return segments;
  }

  // Leading gap if first point is after range start
  const firstTs = new Date(dataPoints[0].timestamp);
  if (firstTs > start) {
    const ms = firstTs.getTime() - start.getTime();
    segments.push({
      startTime: start,
      endTime: firstTs,
      value: null,
      source: null,
      widthPct: (ms / totalMs) * 100,
      color: '#9ca3af',
      noData: true,
    });
  }

  // One segment per data point, lasting until the next change (or end)
  for (let i = 0; i < dataPoints.length; i++) {
    const segStart = new Date(dataPoints[i].timestamp);
    const segEnd = i < dataPoints.length - 1 ? new Date(dataPoints[i + 1].timestamp) : end;
    const ms = segEnd.getTime() - segStart.getTime();
    if (ms <= 0) continue;
    segments.push({
      startTime: segStart,
      endTime: segEnd,
      value: dataPoints[i].value,
      source: dataPoints[i].source ?? null,
      widthPct: (ms / totalMs) * 100,
      color: getSegmentColor(val, dataPoints[i].value),
      noData: false,
    });
  }

  return segments;
}

// ── Color mapping per value type ──────────────────────────────────────────

/** Parse any css color string (hex or rgb(...)) into [r,g,b] 0-255 components */
function parseColor(c: string): [number, number, number] {
  const hex = c.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (hex) return [parseInt(hex[1], 16), parseInt(hex[2], 16), parseInt(hex[3], 16)];
  const rgb = c.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
  if (rgb) return [+rgb[1], +rgb[2], +rgb[3]];
  return [250, 204, 21]; // fallback yellow
}

function getSegmentColor(val: DeviceValue, value: unknown): string {
  const key = val.valueKey.toLowerCase();
  const [ar, ag, ab] = parseColor(props.accentColor);

  // Digital boolean values — use semantic colors based on the key name
  if (val.loggingType === 'Digital') {
    const on = value === true || value === 1 || value === 'true';
    if (key.endsWith('__alarm') || key === 'alarm') {
      return on ? 'rgb(220, 38, 38)' : props.inactiveColor; // red when alarming
    }
    if (key.endsWith('__armed') || key === 'armed') {
      return on ? 'rgb(34, 197, 94)' : props.inactiveColor; // green when armed
    }
    if (key === 'occupancy') {
      return on ? 'rgb(168, 85, 247)' : props.inactiveColor; // purple when occupied
    }
    return on ? props.accentColor : props.inactiveColor;
  }

  if (['power', 'on_off', 'state'].includes(key)) {
    const on = value === true || value === 1 || value === 'on';
    return on ? props.accentColor : props.inactiveColor;
  }

  if (['brightness', 'dimming', 'level'].includes(key)) {
    const pct = typeof value === 'number' ? Math.max(0, Math.min(100, value)) : 0;
    if (pct === 0) return props.inactiveColor;
    const t = pct / 100;
    const r = Math.round(107 + (ar - 107) * t);
    const g = Math.round(114 + (ag - 114) * t);
    const b = Math.round(128 + (ab - 128) * t);
    return `rgb(${r},${g},${b})`;
  }

  if (['color', 'rgb'].includes(key)) {
    if (!value) return '#facc15';
    if (typeof value === 'string' && value.startsWith('#')) return value;
    if (typeof value === 'string') {
      const parts = value.split(',').map(Number);
      if (parts.length >= 3 && !parts.some(isNaN)) return `rgb(${parts[0]},${parts[1]},${parts[2]})`;
    }
    if (Array.isArray(value) && value.length >= 3) return `rgb(${value[0]},${value[1]},${value[2]})`;
    return '#facc15';
  }

  if (['color_temp', 'color_temperature'].includes(key)) {
    const temp = Number(value) || 4000;
    const t = Math.max(0, Math.min(1, (temp - 2000) / (6500 - 2000)));
    const r = Math.round(245 + (59 - 245) * t);
    const g = Math.round(158 + (130 - 158) * t);
    const b = Math.round(11 + (246 - 11) * t);
    return `rgb(${r},${g},${b})`;
  }

  return '#3b82f6';
}

function getSegments(valueId: string): Segment[] {
  return segmentsByValue.value[valueId] ?? [];
}

// ── Formatting ────────────────────────────────────────────────────────────
function formatCurrentValue(val: DeviceValue): string {
  const raw = val.currentValue;
  if (raw === null || raw === undefined) return '—';
  if (raw === true || raw === 1 || raw === 'on') return tt('On', 'Vklopljeno');
  if (raw === false || raw === 0 || raw === 'off') return tt('Off', 'Izklopljeno');
  if (typeof raw === 'number') {
    const dp = val.decimalPlaces ?? 0;
    const unit = val.unit ? ` ${val.unit}` : '';
    return `${raw.toFixed(dp)}${unit}`;
  }
  return String(raw);
}

function formatSegmentValue(seg: Segment, val: DeviceValue): string {
  if (seg.noData) return tt('No prior data', 'Ni predhodnih podatkov');
  const v = seg.value;
  if (v === null || v === undefined) return '—';
  if (v === true || v === 1 || v === 'on') return tt('On', 'Vklopljeno');
  if (v === false || v === 0 || v === 'off') return tt('Off', 'Izklopljeno');
  if (typeof v === 'number') {
    const dp = val.decimalPlaces ?? 0;
    const unit = val.unit ? ` ${val.unit}` : '';
    return `${v.toFixed(dp)}${unit}`;
  }
  if (typeof v === 'string' && val.valueKey.toLowerCase() === 'color') return v.toUpperCase();
  return String(v);
}

function formatTime(date: Date): string {
  return formatTimeLocale(date, { hour: '2-digit', minute: '2-digit' });
}

function formatAxisTime(date: Date): string {
  if (periodMs.value <= 86_400_000) return formatTimeLocale(date, { hour: '2-digit', minute: '2-digit' });
  return formatDate(date, { month: 'short', day: 'numeric' });
}

function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1_000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  if (ms < 86_400_000) return `${(ms / 3_600_000).toFixed(1)}h`;
  return `${(ms / 86_400_000).toFixed(1)}d`;
}

function sourceClass(source: string): string {
  switch (source?.toLowerCase()) {
    case 'user':
    case 'manual':
      return 'bg-blue-500/30 text-blue-300';
    case 'automation':
      return 'bg-purple-500/30 text-purple-300';
    case 'scene':
      return 'bg-amber-500/30 text-amber-300';
    case 'api':
      return 'bg-teal-500/30 text-teal-300';
    default:
      return 'bg-gray-500/30 text-gray-300';
  }
}

// ── Tooltip ───────────────────────────────────────────────────────────────
const tooltip = ref<TooltipState>({
  visible: false,
  x: 0,
  y: 0,
  valueName: '',
  valueLabel: '',
  timeRange: '',
  duration: '',
  source: null,
  color: '#9ca3af',
});

function showTooltip(e: MouseEvent, seg: Segment, val: DeviceValue) {
  const durationMs = seg.endTime.getTime() - seg.startTime.getTime();
  tooltip.value = {
    visible: true,
    x: e.clientX,
    y: e.clientY,
    valueName: val.name,
    valueLabel: formatSegmentValue(seg, val),
    timeRange: seg.noData
      ? tt('Unknown prior state', 'Neznano prejšnje stanje')
      : `${formatTime(seg.startTime)} → ${formatTime(seg.endTime)}`,
    duration: formatDuration(durationMs),
    source: seg.source,
    color: seg.color,
  };
}

function updateTooltipPos(e: MouseEvent) {
  tooltip.value.x = e.clientX;
  tooltip.value.y = e.clientY;
}

function hideTooltip() {
  tooltip.value.visible = false;
}

// ── Watchers ──────────────────────────────────────────────────────────────
watch(
  historyRequestKey,
  () => {
    if (setpointValues.value.length) fetchHistory();
  },
  { immediate: true },
);
</script>
